import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Calculator, Sparkles, Zap, Leaf, Hand, Hospital, TrendingDown, BadgeCheck } from "lucide-react";
import { api } from "../lib/api";

// Reference single-session prices per category (used to compute pack savings)
const SINGLE_REF = {
  ems: 60,        // séance EMS unitaire (decouverte)
  massage: 75,    // séance massage drainage unitaire
  duo: 80,        // duo unitaire (no pack so reference = itself)
};

const CATEGORY_META = {
  ems: {
    label: "EMS",
    longLabel: "Électrostimulation",
    icon: Zap,
    badge: "MIHA BODYTEC",
    description:
      "25 minutes équivalent à 4h de sport classique. Travail global, sans impact articulaire, à votre domicile.",
  },
  massage: {
    label: "Massages",
    longLabel: "Massages & Bien-être",
    icon: Leaf,
    badge: "Soins manuels",
    description:
      "Drainage lymphatique, drainage post-opératoire et combo Drain & Sculpt — des soins manuels personnalisés, à domicile.",
  },
};

export default function Tarifs() {
  const [packs, setPacks] = useState([]);
  const [useCredit, setUseCredit] = useState(true);
  const [category, setCategory] = useState("ems");

  useEffect(() => {
    api.get("/packs").then((r) => setPacks(r.data)).catch(() => {});
  }, []);

  const computePrice = (p) => (useCredit ? Math.round(p.price * 0.5 * 100) / 100 : p.price);

  // Filter packs by category (include "duo" in EMS bucket for now)
  const filtered = useMemo(() => {
    if (category === "ems") {
      return packs.filter((p) => p.category === "ems" || p.category === "duo");
    }
    return packs.filter((p) => p.category === "massage");
  }, [packs, category]);

  // Find single session reference within current category (lowest nb_seances pack)
  const refSingle = SINGLE_REF[category] || 60;

  // Sort: single sessions first, then by nb_seances asc
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => a.nb_seances - b.nb_seances),
    [filtered]
  );

  const isMassage = category === "massage";
  const themeClass = isMassage ? "theme-massage" : "theme-ems";

  return (
    <div className={`tarifs-shell ${themeClass} ${isMassage ? "bg-[#FBF1E6]" : "bg-white"}`}>
      {/* Hero */}
      <section
        className={`pt-16 pb-12 px-6 lg:px-10 tarifs-shell ${
          isMassage ? "bg-[#F2E0CC]" : "bg-fegray-light"
        }`}
        data-testid="tarifs-hero"
      >
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-theme-deep font-medium uppercase tracking-[0.25em] text-xs mb-4">
            — Nos tarifs
          </p>
          <h1 className="text-4xl lg:text-6xl font-bold text-fegray-dark mb-4">
            Choisissez votre <span className="text-theme-primary">{CATEGORY_META[category].longLabel}</span>
          </h1>
          <p className="mt-5 text-fegray max-w-2xl mx-auto leading-relaxed">
            {CATEGORY_META[category].description}
          </p>

          {/* Category Switcher */}
          <div
            className="mt-10 inline-flex bg-white rounded-full p-1.5 shadow-lg border border-gray-100"
            data-testid="category-switcher"
          >
            <CategoryButton
              active={category === "ems"}
              onClick={() => setCategory("ems")}
              activeColor="#84C342"
              data-testid="cat-ems"
            >
              <Zap className="w-4 h-4" /> EMS
            </CategoryButton>
            <CategoryButton
              active={category === "massage"}
              onClick={() => setCategory("massage")}
              activeColor="#C49B7C"
              data-testid="cat-massage"
            >
              <Leaf className="w-4 h-4" /> Massages
            </CategoryButton>
          </div>
        </div>
      </section>

      {/* Pack advantage banner */}
      <section className="px-6 lg:px-10 mt-10">
        <div
          className="max-w-7xl mx-auto fe-card p-6 lg:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-l-4 border-theme-primary bg-theme-tint"
          data-testid="pack-advantage-banner"
        >
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-sm flex-shrink-0">
              <TrendingDown className="w-7 h-7 text-theme-primary" />
            </div>
            <div>
              <div className="text-lg lg:text-xl font-extrabold text-fegray-dark mb-1">
                Les packs d'heures sont jusqu'à <span className="text-theme-primary">−25% moins chers</span> que les séances à l'unité
              </div>
              <p className="text-sm text-fegray max-w-2xl">
                Plus vous prenez de séances en une fois, plus le tarif par séance diminue.
                Idéal pour s'engager dans une vraie routine — et cumuler l'effet du crédit d'impôt 50%.
              </p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-wider text-fegray font-bold">Économie max</div>
            <div className="text-3xl lg:text-4xl font-extrabold text-theme-primary">−25%</div>
            <div className="text-[10px] text-fegray/70">vs séance unique</div>
          </div>
        </div>
      </section>

      {/* Calculator toggle */}
      <section className="py-10 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto fe-card p-6 lg:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-theme-tint flex items-center justify-center">
              <Calculator className="w-7 h-7 text-theme-primary" />
            </div>
            <div>
              <div className="text-lg font-bold text-fegray-dark">Crédit d'impôt 50%</div>
              <p className="text-sm text-fegray mt-1">
                Affichez vos tarifs avec ou sans déduction immédiate URSSAF.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-full p-1 shadow-sm border border-gray-100">
            <button
              onClick={() => setUseCredit(false)}
              data-testid="toggle-price-full"
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                !useCredit ? "bg-fegray text-white" : "text-fegray hover:text-fegray-dark"
              }`}
            >
              Prix TTC
            </button>
            <button
              onClick={() => setUseCredit(true)}
              data-testid="toggle-price-net"
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                useCredit ? "bg-theme-primary text-white" : "text-fegray hover:text-fegray-dark"
              }`}
            >
              Reste à charge −50%
            </button>
          </div>
        </div>
      </section>

      {/* Packs */}
      <section className="py-10 lg:py-14 px-6 lg:px-10" data-testid="packs-grid">
        <div className="max-w-7xl mx-auto">
          {isMassage ? (
            <MassageGrouped
              packs={sorted}
              computePrice={computePrice}
              useCredit={useCredit}
              refSingle={refSingle}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {sorted.map((p, i) => (
                <PackCard
                  key={p.id}
                  pack={p}
                  index={i}
                  computePrice={computePrice}
                  useCredit={useCredit}
                  refSingle={refSingle}
                  isMassage={false}
                />
              ))}
            </div>
          )}

          {sorted.length === 0 && (
            <div className="text-center py-12 text-fegray">Aucune offre disponible.</div>
          )}
        </div>
      </section>

      {/* Objectifs (only on EMS) */}
      {!isMassage && (
        <section className="py-14 px-6 lg:px-10 bg-fegray-light">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold text-fegray-dark text-center mb-2">
              Une offre sur mesure !
            </h2>
            <p className="text-center text-fegreen font-medium mb-12">
              En fonction de vos objectifs
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: "Reprise d'activité", icon: "🌱", points: ["Retrouver la motivation", "Être accompagné"] },
                { title: "Sport & Performance", icon: "🏆", points: ["Préparation physique sportive", "Amélioration de l'endurance", "Développer ses muscles"] },
                { title: "Remise en forme*", icon: "✨", points: ["Perdre du poids", "Se tonifier"] },
                { title: "Santé & Bien-être", icon: "💚", points: ["Réduire les douleurs du dos", "Travail du périnée"] },
              ].map((c, i) => (
                <div key={i} className="fe-card p-7 text-center">
                  <div className="text-4xl mb-4">{c.icon}</div>
                  <h3 className="text-lg font-bold text-fegray-dark mb-4">{c.title}</h3>
                  <ul className="space-y-2 text-sm text-fegray text-left">
                    {c.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <span className="text-fegreen font-bold">➤</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="text-xs text-fegray/60 italic text-center mt-8">
              * Sous réserve d'une alimentation équilibrée et de prise en compte des conseils
            </p>
          </div>
        </section>
      )}

      {/* Massage bienfaits (only on massage) */}
      {isMassage && (
        <section className="py-14 px-6 lg:px-10 bg-[#F2E0CC]/40">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold text-fegray-dark text-center mb-2">
              Des soins pour chaque besoin
            </h2>
            <p className="text-center text-theme-deep font-medium mb-12">
              Mains expertes & techniques douces
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: "Drainage lymphatique", icon: "💧", points: ["Jambes légères", "Détox & rétention d'eau"] },
                { title: "Drainage post-opératoire", icon: "🩹", points: ["Cicatrisation accompagnée", "Adapté aux suites chirurgicales"] },
                { title: "Drain & Sculpt", icon: "✨", points: ["Affinement ciblé", "Combo drainage + madérothérapie"] },
                { title: "Pack post-op 4 semaines", icon: "🤲", points: ["8 séances réparties", "Tarif préférentiel"] },
              ].map((c, i) => (
                <div key={i} className="fe-card p-7 text-center bg-white">
                  <div className="text-4xl mb-4">{c.icon}</div>
                  <h3 className="text-lg font-bold text-fegray-dark mb-4">{c.title}</h3>
                  <ul className="space-y-2 text-sm text-fegray text-left">
                    {c.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <span className="text-theme-primary font-bold">➤</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Vidéo Miha (only on EMS) */}
      {!isMassage && (
        <section className="py-14 px-6 lg:px-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-3">
                — Découvrez la technologie
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-fegray-dark">
                MIHA BODYTEC en vidéo
              </h2>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg aspect-video">
              <iframe
                src="https://www.youtube.com/embed/FPtqhHgwi90"
                title="MIHA BODYTEC — Électrostimulation"
                allow="encrypted-media; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function PackCard({ pack: p, index: i, computePrice, useCredit, refSingle, isMassage }) {
  const perSession = p.price / p.nb_seances;
  const savePerSession = refSingle - perSession;
  const savePct = refSingle > 0 ? Math.round((savePerSession / refSingle) * 100) : 0;
  const isBestDeal = p.nb_seances > 1 && savePct >= 15;
  const isSingle = p.nb_seances === 1;

  return (
    <div
      data-testid={`pack-card-${p.id}`}
      className={`fe-card p-7 flex flex-col relative bg-white ${
        p.popular || isBestDeal ? "ring-2 ring-theme-primary" : ""
      }`}
    >
      {p.popular && (
        <div className="absolute -top-3 left-7 bg-theme-primary text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-md">
          <Sparkles className="w-3 h-3" /> Populaire
        </div>
      )}
      {!p.popular && isBestDeal && (
        <div className="absolute -top-3 left-7 bg-fegray-dark text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-md">
          <BadgeCheck className="w-3 h-3" /> Meilleur tarif
        </div>
      )}

      <p className="text-xs uppercase tracking-wider text-theme-primary font-bold">
        0{i + 1} · {p.nb_seances} séance{p.nb_seances > 1 ? "s" : ""}
      </p>
      <h3 className="text-xl font-bold text-fegray-dark mt-3">{p.name}</h3>
      <p className="text-sm text-fegray mt-2 min-h-[44px]">{p.description}</p>

      <div className="mt-5 pt-5 border-t border-gray-100">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-4xl font-extrabold text-theme-primary">{computePrice(p)}€</span>
          {useCredit && (
            <span className="text-sm text-fegray/40 line-through">{p.price}€</span>
          )}
        </div>
        <div className="text-xs text-fegray mt-2">
          {useCredit ? "après crédit d'impôt 50%" : "tarif TTC"}
          {p.nb_seances > 1 && (
            <> · <span className="font-semibold">{(computePrice(p) / p.nb_seances).toFixed(2)}€ / séance</span></>
          )}
        </div>

        {p.nb_seances > 1 && savePct > 0 && (
          <div className="mt-3 inline-flex items-center gap-1.5 bg-theme-tint text-theme-deep text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
            <TrendingDown className="w-3 h-3" /> Économie −{savePct}% / séance
          </div>
        )}
        {isSingle && p.category !== "duo" && !isMassage && (
          <div className="mt-3 inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 text-[11px] font-semibold px-3 py-1.5 rounded-full">
            Séance à l'unité — réf. tarif plein
          </div>
        )}
      </div>

      <ul className="space-y-2 mt-5 mb-6 flex-1">
        {(p.features || []).map((f, j) => (
          <li key={j} className="flex items-start gap-2 text-sm text-fegray">
            <Check className="w-4 h-4 mt-0.5 text-theme-primary flex-shrink-0" strokeWidth={2.5} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Link
        to={`/reservation?pack=${p.id}`}
        className={`mt-auto ${p.popular || isBestDeal ? "btn-theme" : "btn-theme-outline"} w-full`}
        data-testid={`pack-reserve-${p.id}`}
      >
        Réserver <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

const MASSAGE_GROUPS = [
  {
    key: "drainage",
    label: "Drainage Lymphatique",
    icon: Leaf,
    description: "Massage manuel détox — élimination toxines, jambes légères",
  },
  {
    key: "postop",
    label: "Drainage Post-Opératoire",
    icon: Hospital,
    description: "Abdominoplastie · Liposuccion · BBL — accompagnement cicatrisation",
  },
  {
    key: "drainsculpt",
    label: "Drain & Sculpt",
    icon: Hand,
    description: "Combo Drainage + Madérothérapie — affinement & sculpting",
  },
];

function MassageGrouped({ packs, computePrice, useCredit, refSingle }) {
  return (
    <div className="space-y-14">
      {MASSAGE_GROUPS.map((group) => {
        const groupPacks = packs.filter((p) => p.subcategory === group.key);
        if (groupPacks.length === 0) return null;
        const Icon = group.icon;
        return (
          <div key={group.key} data-testid={`massage-group-${group.key}`}>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-theme-tint flex items-center justify-center shrink-0">
                <Icon className="w-7 h-7 text-theme-deep" strokeWidth={1.8} />
              </div>
              <div>
                <h2 className="text-2xl lg:text-3xl font-bold text-fegray-dark">{group.label}</h2>
                <p className="text-sm text-fegray mt-1">{group.description}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groupPacks.map((p, i) => (
                <PackCard
                  key={p.id}
                  pack={p}
                  index={i}
                  computePrice={computePrice}
                  useCredit={useCredit}
                  refSingle={refSingle}
                  isMassage={true}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CategoryButton({ active, onClick, activeColor, children, ...rest }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all ${
        active ? "text-white shadow-md" : "text-fegray hover:text-fegray-dark"
      }`}
      style={active ? { backgroundColor: activeColor } : {}}
      {...rest}
    >
      {children}
    </button>
  );
}
